import { useState } from 'react';
import LogService, { LOG_ACTIONS } from '../services/LogService';

const CATEGORIES = ['Estrutural', 'Arquitetônico', 'Hidráulico', 'Elétrico', 'Louças e Metais', 'Pintura', 'Revestimento', 'Outro'];
const UNITS = ['un', 'm²', 'm', 'm³', 'kg', 'L', 'peça', 'vb'];

function TabQuantitativos({ data = [], resumo = {}, onAddItem }) {
  const [feedbackState, setFeedbackState] = useState('idle'); // idle | negative | sent
  const [feedbackText, setFeedbackText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newItem, setNewItem] = useState({ categoria: 'Estrutural', elemento: '', quantidade: '', unidade: 'un', observacao: '' });

  const handlePositive = () => {
    LogService.log(LOG_ACTIONS.FEEDBACK_POSITIVE, {
      details: { itemCount: data.length, resumo },
    });
    setFeedbackState('sent');
  };

  const handleNegative = () => {
    setFeedbackState('negative');
  };

  const handleSendFeedback = () => {
    if (!feedbackText.trim()) return;
    LogService.log(LOG_ACTIONS.FEEDBACK_NEGATIVE, {
      details: { feedback: feedbackText.trim(), itemCount: data.length, resumo },
    });
    setFeedbackState('sent');
    setFeedbackText('');
  };

  const handleAddItem = () => {
    if (!newItem.elemento.trim() || !newItem.quantidade) return;
    const item = {
      elemento: newItem.elemento.trim(),
      categoria: newItem.categoria,
      quantidade: parseFloat(newItem.quantidade),
      unidade: newItem.unidade,
      manual: true,
      observacao: newItem.observacao.trim(),
    };
    LogService.log(LOG_ACTIONS.MANUAL_ITEM_ADD, {
      details: item,
    });
    if (onAddItem) onAddItem(item);
    setNewItem({ categoria: 'Estrutural', elemento: '', quantidade: '', unidade: 'un', observacao: '' });
    setShowModal(false);
  };

  return (
    <div className="p-4 md:p-8">
      {/* Resumo do projeto */}
      {resumo && resumo.tipo_projeto && (
        <div className="mb-6 p-4 bg-orca-50 rounded-xl border border-orca-100">
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Tipo de Projeto</p>
              <p className="font-semibold text-orca-800">{resumo.tipo_projeto}</p>
            </div>
            {resumo.area_total > 0 && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Área Total</p>
                <p className="font-semibold text-orca-800">{resumo.area_total} m²</p>
              </div>
            )}
            {resumo.descricao && (
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Descrição</p>
                <p className="font-medium text-gray-700 text-sm">{resumo.descricao}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Quantitativos Extraídos</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
            {data.length} itens
          </span>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orca-700 bg-orca-50 border border-orca-200 rounded-lg hover:bg-orca-100 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Adicionar item
          </button>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg">Nenhum quantitativo extraído</p>
          <p className="text-sm mt-2">Tente enviar uma planta com mais detalhes</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="w-full min-w-[500px]">
            <thead>
              <tr className="border-b-2 border-gray-100">
                <th className="text-left py-4 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Elemento</th>
                <th className="text-left py-4 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Categoria</th>
                <th className="text-right py-4 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Quantidade</th>
                <th className="text-right py-4 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Unidade</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-50 hover:bg-orca-50/30 transition-colors"
                >
                  <td className="py-4 px-4">
                    <span className="font-medium text-gray-900 text-base">{item.elemento}</span>
                    {item.manual && (
                      <span className="ml-2 inline-block text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                        MANUAL
                      </span>
                    )}
                    {item.observacao && (
                      <p className="text-xs text-gray-400 mt-0.5">{item.observacao}</p>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-xs font-medium text-orca-700 bg-orca-50 px-2 py-1 rounded-md">{item.categoria}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="text-lg font-bold text-gray-900">
                      {typeof item.quantidade === 'number' ? item.quantidade.toLocaleString('pt-BR') : item.quantidade}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="text-gray-500">{item.unidade}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Feedback Section */}
      {data.length > 0 && (
        <div className="mt-8 pt-6 border-t border-gray-100">
          {feedbackState === 'idle' && (
            <div className="text-center">
              <p className="text-gray-600 font-medium mb-4">O resultado foi satisfatório?</p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={handlePositive}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-xl hover:bg-green-100 cursor-pointer transition-colors"
                >
                  <span className="text-lg">👍</span> Sim, está bom
                </button>
                <button
                  onClick={handleNegative}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 cursor-pointer transition-colors"
                >
                  <span className="text-lg">👎</span> Faltou algo
                </button>
              </div>
            </div>
          )}

          {feedbackState === 'negative' && (
            <div className="max-w-lg mx-auto">
              <p className="text-gray-600 font-medium mb-3 text-center">O que faltou ou está errado?</p>
              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Descreva o que ficou faltando ou incorreto na análise..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent focus:bg-white transition-all resize-none"
              />
              <div className="flex justify-end gap-2 mt-3">
                <button
                  onClick={() => setFeedbackState('idle')}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendFeedback}
                  disabled={!feedbackText.trim()}
                  className="px-5 py-2 text-sm font-medium text-white bg-orca-800 rounded-lg hover:bg-orca-700 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Enviar feedback
                </button>
              </div>
            </div>
          )}

          {feedbackState === 'sent' && (
            <div className="text-center py-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-xl">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-medium text-green-700">Obrigado pelo feedback!</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Item Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Adicionar item</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Categoria</label>
                <select
                  value={newItem.categoria}
                  onChange={(e) => setNewItem((p) => ({ ...p, categoria: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent cursor-pointer"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Item</label>
                <input
                  type="text"
                  value={newItem.elemento}
                  onChange={(e) => setNewItem((p) => ({ ...p, elemento: e.target.value }))}
                  placeholder="Ex: Vaso sanitário"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent focus:bg-white transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Quantidade</label>
                  <input
                    type="number"
                    value={newItem.quantidade}
                    onChange={(e) => setNewItem((p) => ({ ...p, quantidade: e.target.value }))}
                    placeholder="0"
                    min="0"
                    step="any"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent focus:bg-white transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Unidade</label>
                  <select
                    value={newItem.unidade}
                    onChange={(e) => setNewItem((p) => ({ ...p, unidade: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent cursor-pointer"
                  >
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Observação <span className="text-gray-400 font-normal">(opcional)</span></label>
                <input
                  type="text"
                  value={newItem.observacao}
                  onChange={(e) => setNewItem((p) => ({ ...p, observacao: e.target.value }))}
                  placeholder="Ex: Não identificado na planta"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent focus:bg-white transition-all"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddItem}
                disabled={!newItem.elemento.trim() || !newItem.quantidade}
                className="flex-1 px-4 py-3 text-sm font-bold text-white bg-orca-800 rounded-xl hover:bg-orca-700 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TabQuantitativos;
